import { Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { CalcsScenarioDataSetupService } from './calcs-scenario-data-setup.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  extractObject,
  generalConfigFromGlobal,
  normalizeFilterParams
} from '../../../shared/workspace-element-context';
import * as _ from 'lodash-es';
import { Subscription } from 'rxjs';

@Component({
    selector: 'gtw-calcs-scenario-data-setup',
    templateUrl: './calcs-scenario-data-setup.component.html',
    styleUrls: ['./calcs-scenario-data-setup.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CalcsScenarioDataSetupComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  private static readonly bridgeHostSelector = 'gtw-calcs-scenario-data-setup-ce';

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};

  private subscriptions = new Subscription();
  /** Invalidates in-flight scenario GET handlers after context/tab churn or a newer fetch wave. */
  private fetchWave = 0;
  private saveWave = 0;
  private copyInFlight = false;

  // Component properties following F7004 pattern
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  
  showJsonLoader: boolean = false;
  taxYearsList: any[] = [];
  tax_year: any = {};
  FromScenarioList: any[] = [];
  ToScenarioList: any[] = [];
  FromFilingGroupList: any[] = [];
  ToFilingGroupList: any[] = [];

  fromTaxYearFlag: boolean = false;
  toTaxYearFlag: boolean = false;

  selectedFromTaxYear: any = null;
  selectedToTaxYear: any = null;
  selectedFromScenario: any = null;
  selectedToScenario: any = null;
  selectedFromFilingGroup: any = null;
  selectedToFilingGroup: any = null;

  clientKey: string = '';

  constructor(
    private gtwSnackbarService: GtwSnackbarService,
    private calcsScenarioDataSetupService: CalcsScenarioDataSetupService,
    private globalServiceProvider: GlobalService,
    private workspaceService: WorkspaceService,
    private appService: AppService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.subscriptions.add(this.workspaceService.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subscriptions.add(this.globalServiceProvider.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private nextFetchWave(): number {
    return ++this.fetchWave;
  }

  private nextSaveWave(): number {
    return ++this.saveWave;
  }

  private emitRequestGridUpdate(detail: 'refresh' | 'cancel'): void {
    this.requestGridUpdate.emit(detail);
    const host = document.querySelector(CalcsScenarioDataSetupComponent.bridgeHostSelector);
    host?.dispatchEvent(new CustomEvent('request-grid-update', { detail, bubbles: true }));
  }

  private resolveApiBase(): string {
    const gc = this.GENERAL_CONFIG || {};
    return String(gc.base_url || gc.api || '').trim();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.workspaceService.workspaceFactory?.activeScreen || {};
    this.baseURLs = generalConfigFromGlobal(this.globalServiceProvider);
    this.GENERAL_CONFIG = this.baseURLs;

    let fp = normalizeFilterParams(activeScreen) || {};
    if (!Object.keys(fp).length) {
      fp = extractObject(this.globalServiceProvider.global?.globalParams || {});
    }

    this.globalService = this.globalServiceProvider.hydrateGlobalParamsFromContext(activeScreen, fp);

    this.USER_SETTINGS = this.appService.USER_SETTINGS || {};
    this.SERVER_CONFIG = this.appService.SERVER_CONFIG || {};

    const appTaxYears = this.appService.TAX_YEARS;
    this.tax_year = (appTaxYears && typeof appTaxYears === 'object') ? appTaxYears : {};
  }

  private tryInit(): void {
    const apiBase = this.resolveApiBase();
    if (!apiBase) {
      return;
    }
    if (!this.globalService || Object.keys(this.globalService).length === 0) {
      return;
    }
    this.init();
  }

  init(): void {
    if (this.baseURLs && Object.keys(this.baseURLs).length > 0 && 
        this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0 &&
        this.globalService && Object.keys(this.globalService).length > 0) {
      this.initializeData();
    }
  }

  initializeData(): void {
    const wave = this.nextFetchWave();

    if (this.USER_SETTINGS?.user?.client_key) {
      this.clientKey = this.USER_SETTINGS.user.client_key;
    }

    this.initializeTaxYears(wave);
  }

  initializeTaxYears(wave: number): void {
    this.fetchTaxYears(wave);
  }

  fetchTaxYears(wave: number): void {
    if (this.tax_year && Object.keys(this.tax_year).length > 0) {
      this.taxYearsList = this.normalizeTaxYears(this.tax_year);
      if (!this.taxYearsList.length) {
        this.createDefaultTaxYearsList(wave);
        return;
      }

      const currentTaxYear = String(this.globalService?.tax_year ?? '');
      const selectedTaxYear =
        this.taxYearsList.find((item: any) =>
          String(item?.label ?? '') === currentTaxYear || String(item?.value ?? '') === currentTaxYear
        ) || this.taxYearsList[0];

      this.selectedFromTaxYear = selectedTaxYear;
      this.selectedToTaxYear = selectedTaxYear;

      if (this.selectedFromTaxYear) {
        this.fetchFromScenario(this.selectedFromTaxYear.value, this.selectedFromTaxYear.scenario, wave);
        this.fetchToScenario(this.selectedFromTaxYear.value, this.selectedFromTaxYear.scenario, wave);
      }
    } else {
      this.createDefaultTaxYearsList(wave);
    }
  }

  private normalizeTaxYears(source: any): any[] {
    const normalized: any[] = [];
    const defaultScenario = String(this.globalService?.scenario ?? '74');

    const pushNormalized = (entry: any, keyHint?: string): void => {
      if (entry === null || entry === undefined) {
        return;
      }

      if (Array.isArray(entry)) {
        entry.forEach((inner: any) => pushNormalized(inner, keyHint));
        return;
      }

      if (typeof entry === 'object') {
        const label = entry.label ?? entry.value ?? keyHint;
        const value = entry.value ?? entry.label ?? keyHint;
        if (label === null || label === undefined || label === '') {
          return;
        }
        normalized.push({
          ...entry,
          label: String(label),
          value: String(value),
          scenario: String(entry.scenario ?? defaultScenario)
        });
        return;
      }

      if (keyHint !== undefined) {
        normalized.push({
          label: String(keyHint),
          value: String(entry || keyHint),
          scenario: defaultScenario
        });
      }
    };

    if (Array.isArray(source)) {
      source.forEach((item: any) => pushNormalized(item));
    } else {
      _.forEach(source, (value: any, key: string) => pushNormalized(value, key));
    }

    return _.uniqBy(normalized, (item: any) => `${item.value}|${item.scenario}`);
  }

  createDefaultTaxYearsList(wave: number): void {
    const currentYear = new Date().getFullYear();
    const taxYears: any[] = [];
    for (let i = currentYear - 5; i <= currentYear + 1; i++) {
      taxYears.push({
        label: i.toString(),
        value: i.toString(),
        scenario: this.globalService?.scenario || '74'
      });
    }
    this.taxYearsList = taxYears;
    
    const currentTaxYear = String(this.globalService?.tax_year ?? '');
    const selectedTaxYear =
      this.taxYearsList.find((item: any) =>
        String(item?.label ?? '') === currentTaxYear || String(item?.value ?? '') === currentTaxYear
      ) || this.taxYearsList[0];
    this.selectedFromTaxYear = selectedTaxYear;
    this.selectedToTaxYear = selectedTaxYear;

    if (this.selectedFromTaxYear) {
      this.fetchFromScenario(this.selectedFromTaxYear.value, this.selectedFromTaxYear.scenario, wave);
      this.fetchToScenario(this.selectedFromTaxYear.value, this.selectedFromTaxYear.scenario, wave);
    }
  }

  fetchFromScenario(taxYear: string, scenario: string, wave: number): void {
    this.showJsonLoader = true;
    this.fromTaxYearFlag = true;
    
    const params = {
      tax_year: taxYear,
      scenario: scenario,
      jcd_key: this.globalService?.jcd_key || '250'
    };

    const apiBase = this.resolveApiBase();
    if (!apiBase) {
      this.fromTaxYearFlag = false;
      this.showJsonLoader = false;
      return;
    }

    this.calcsScenarioDataSetupService.getFromScenario(
      apiBase,
      params
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (wave !== this.fetchWave) {
          return;
        }
        if (data && data.errorMessage == '' && data.jsonObject) {
          this.FromScenarioList = data.jsonObject;
          const selectedScenarioIndex = _.findIndex(this.FromScenarioList, { 'VALUE': parseInt(scenario, 10) });
          if (selectedScenarioIndex >= 0) {
            this.selectedFromScenario = this.FromScenarioList[selectedScenarioIndex];
          }
        }
        this.showJsonLoader = false;
        this.fromTaxYearFlag = false;
      },
      (error: any) => {
        if (wave !== this.fetchWave) {
          return;
        }
        console.error('Error fetching from scenario:', error);
        this.showJsonLoader = false;
        this.fromTaxYearFlag = false;
        this.gtwSnackbarService.openSnackBar('Error fetching from scenario data', 'gtw-snackbar--red');
      }
    );
  }

  fetchToScenario(taxYear: string, scenario: string, wave: number): void {
    this.showJsonLoader = true;
    this.toTaxYearFlag = true;
    
    const params = {
      tax_year: taxYear,
      scenario: scenario,
      jcd_key: this.globalService?.jcd_key || '250'
    };

    const apiBase = this.resolveApiBase();
    if (!apiBase) {
      this.toTaxYearFlag = false;
      this.showJsonLoader = false;
      return;
    }

    this.calcsScenarioDataSetupService.getFromScenario(
      apiBase,
      params
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (wave !== this.fetchWave) {
          return;
        }
        if (data && data.errorMessage == '' && data.jsonObject) {
          this.ToScenarioList = data.jsonObject;
          const selectedScenarioIndex = _.findIndex(this.ToScenarioList, { 'VALUE': parseInt(scenario, 10) });
          if (selectedScenarioIndex >= 0) {
            this.selectedToScenario = this.ToScenarioList[selectedScenarioIndex];
          }
        }
        this.showJsonLoader = false;
        this.toTaxYearFlag = false;
      },
      (error: any) => {
        if (wave !== this.fetchWave) {
          return;
        }
        console.error('Error fetching to scenario:', error);
        this.showJsonLoader = false;
        this.toTaxYearFlag = false;
        this.gtwSnackbarService.openSnackBar('Error fetching to scenario data', 'gtw-snackbar--red');
      }
    );
  }

  fromTaxYearChange(taxYear: any): void {
    if (taxYear) {
      const wave = this.nextFetchWave();
      this.fromTaxYearFlag = true;
      this.fetchFromScenario(taxYear.value, taxYear.scenario, wave);
    }
  }

  toTaxYearChange(taxYear: any): void {
    if (taxYear) {
      const wave = this.nextFetchWave();
      this.toTaxYearFlag = true;
      this.fetchToScenario(taxYear.value, taxYear.scenario, wave);
    }
  }

  copySelectedChanges(): void {
    if (this.copyInFlight) {
      return;
    }
    if (!this.selectedFromTaxYear || !this.selectedFromScenario || 
        !this.selectedToTaxYear || !this.selectedToScenario) {
      this.gtwSnackbarService.openSnackBar('Please select all required fields', 'gtw-snackbar--yellow');
      return;
    }

    const apiBase = this.resolveApiBase();
    if (!apiBase) {
      this.gtwSnackbarService.openSnackBar('API base URL is not configured.', 'gtw-snackbar--red');
      return;
    }

    this.copyInFlight = true;
    this.showJsonLoader = true;
    const saveWave = this.nextSaveWave();
    
    const tempObj = _.merge({}, this.globalService || {});

    const params: any = {
      is_issue_key_enabled: this.globalService.is_issue_key_enabled || 'N',
      scenarioCode: this.globalService.scenarioCode || 'RAF',
      from_tax_year: this.selectedFromTaxYear.value,
      from_scenario: this.selectedFromScenario.VALUE,
      to_tax_year: this.selectedToTaxYear.value,
      to_scenario: this.selectedToScenario.VALUE,
      tax_year: this.globalService?.tax_year,
      scenario: this.globalService?.scenario,
      jcd_key: this.globalService?.jcd_key || '250'
    };

    const mergedParams = _.merge({}, tempObj, params);

    this.calcsScenarioDataSetupService.saveCopyChanges(
      apiBase,
      mergedParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (saveWave !== this.saveWave) {
          return;
        }
        this.showJsonLoader = false;
        this.copyInFlight = false;
        
        if (data && data.callSuccess == "1") {
          this.gtwSnackbarService.openSnackBar('Filing group data copied successfully.', 'gtw-snackbar--green');
          this.emitRequestGridUpdate('refresh');
        } else {
          const errorMsg = data?.errorMessage || 'Error while copying filing group data.';
          this.gtwSnackbarService.openSnackBar(errorMsg, 'gtw-snackbar--red');
        }
      },
      (error: any) => {
        if (saveWave !== this.saveWave) {
          return;
        }
        console.error('Error copying changes:', error);
        this.showJsonLoader = false;
        this.copyInFlight = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while copying data. Please try again.', 'gtw-snackbar--red');
      }
    );
  }
}
